import { useState } from "react";
import { Wifi, User, Phone, Mail, Instagram, MessageCircle, Shield } from "lucide-react";

function TikTokIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.86a8.1 8.1 0 004.77 1.52V6.9a4.85 4.85 0 01-1-.21z"/>
    </svg>
  );
}

function GoogleIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function StarIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#FBBC05" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  );
}

function Field({ icon: Icon, label, focused, field, onFocus, onBlur, ...rest }) {
  const on = focused === field;
  return (
    <div>
      <label style={{ display: "block", fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#374151", marginBottom: "5px", fontFamily: "Inter,sans-serif", fontWeight: 700 }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <Icon size={15} style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: on ? "#D32F2F" : "#9CA3AF", pointerEvents: "none", transition: "color .2s" }} />
        <input className="sc-input" onFocus={() => onFocus(field)} onBlur={onBlur}
          style={{ width: "100%", boxSizing: "border-box", padding: "13px 13px 13px 40px", borderRadius: "10px", fontSize: "0.95rem", fontFamily: "Inter,sans-serif", color: "#111827", background: "#FAFAFA", border: `1.5px solid ${on ? "#D32F2F" : "#E5E7EB"}`, outline: "none", transition: "border-color .2s, background .2s" }}
          {...rest} />
      </div>
    </div>
  );
}

function SocialBtn({ label, icon, active, bg, activeBg, border, activeBorder, color, onClick }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 8px", borderRadius: "10px", fontSize: "0.77rem", fontWeight: 600, fontFamily: "Inter,sans-serif", cursor: "pointer", background: active ? activeBg : bg, border: `1.5px solid ${active ? activeBorder : border}`, color, userSelect: "none", transition: "all .15s" }}>
      {icon} {label}
    </div>
  );
}

export default function SocialConnectForm({ config, onSubmit, submitting }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", marketing_consent: false });
  const [clicks, setClicks] = useState({ instagram_clicked: false, tiktok_clicked: false, google_review_clicked: false });
  const [focused, setFocused] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const clickSocial = (k, url) => { setClicks((s) => ({ ...s, [k]: true })); window.open(url, "_blank", "noopener,noreferrer"); };

  const doSubmit = async () => {
    if (submitting || !canSubmit) return;
    setError(null);
    try { await onSubmit({ ...form, ...clicks }); }
    catch (err) { setError(err.message || "Error al registrar. Inténtalo de nuevo."); }
  };

  const canSubmit = form.name.trim().length >= 2 && form.phone.trim().length >= 6 && form.marketing_consent && !submitting;
  const hasSocial = config?.instagram_url || config?.tiktok_url || config?.google_review_url || config?.whatsapp_number;
  const hasOffer = config?.special_offer_enabled && config?.special_offer_title;
  const offerColor = config?.special_offer_color || "#D32F2F";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

      {/* Special offer teaser */}
      {hasOffer && (
        <div style={{ padding: "16px 18px", borderRadius: "14px", textAlign: "center", background: `${offerColor}10`, border: `1.5px dashed ${offerColor}55` }}>
          <p style={{ fontSize: "0.58rem", letterSpacing: "0.16em", textTransform: "uppercase", color: offerColor, fontFamily: "Inter,sans-serif", fontWeight: 700, margin: "0 0 4px" }}>Oferta especial</p>
          <p style={{ fontSize: "1.05rem", fontWeight: 700, color: "#111827", fontFamily: "Inter,sans-serif", margin: 0 }}>{config.special_offer_title}</p>
          {config.special_offer_description && <p style={{ fontSize: "0.78rem", color: "#6B7280", fontFamily: "Inter,sans-serif", marginTop: "3px" }}>{config.special_offer_description}</p>}
          {config.special_offer_expiration && <p style={{ fontSize: "0.67rem", color: offerColor, fontFamily: "Inter,sans-serif", marginTop: "3px" }}>Válido hasta: {config.special_offer_expiration}</p>}
        </div>
      )}

      {/* Main card */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: "18px", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
        <div style={{ height: "3px", background: "linear-gradient(90deg,#D32F2F,#B71C1C)" }} />
        <div style={{ padding: "22px 18px" }}>

          {/* Steps */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "22px" }}>
            {[["1","Regístrate",true],["2","Recibe clave",false],["3","Conéctate",false]].map(([n,lbl,on],i) => (
              <div key={n} style={{ display: "flex", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                  <div style={{ width: "30px", height: "30px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, fontFamily: "Inter,sans-serif", backgroundColor: on ? "#D32F2F" : "#F3F4F6", color: on ? "#FFFFFF" : "#9CA3AF", border: on ? "none" : "1px solid #E5E7EB" }}>{n}</div>
                  <span style={{ fontSize: "0.52rem", color: on ? "#D32F2F" : "#9CA3AF", fontFamily: "Inter,sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center", width: "62px", lineHeight: 1.2 }}>{lbl}</span>
                </div>
                {i < 2 && <div style={{ width: "16px", height: "1px", background: "#E5E7EB", marginBottom: "16px", flexShrink: 0 }} />}
              </div>
            ))}
          </div>

          {/* Instruction */}
          <p style={{ textAlign: "center", fontSize: "0.78rem", color: "#6B7280", fontFamily: "Inter,sans-serif", marginBottom: "18px", lineHeight: 1.4 }}>
            Rellena tu nombre y WhatsApp para recibir la clave Wi-Fi.
          </p>

          <form onSubmit={(e) => { e.preventDefault(); doSubmit(); }} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Field icon={User} label="Nombre *" field="name" focused={focused} onFocus={setFocused} onBlur={() => setFocused(null)} required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Tu nombre" />
            <Field icon={Phone} label="WhatsApp *" field="phone" focused={focused} onFocus={setFocused} onBlur={() => setFocused(null)} required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+34 612 345 678" />
            <Field icon={Mail} label="Email (opcional)" field="email" focused={focused} onFocus={setFocused} onBlur={() => setFocused(null)} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@ejemplo.com" />

            {hasSocial && (
              <div>
                <p style={{ fontSize: "0.6rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#9CA3AF", marginBottom: "7px", fontFamily: "Inter,sans-serif", fontWeight: 700 }}>Síguenos (opcional)</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: "7px" }}>
                  {config.instagram_url && (
                    <SocialBtn label="Instagram" icon={<Instagram size={13}/>}
                      active={clicks.instagram_clicked}
                      bg="#FFFFFF" activeBg="rgba(225,48,108,0.06)"
                      border="#E5E7EB" activeBorder="rgba(225,48,108,0.5)"
                      color="#DB2777"
                      onClick={() => clickSocial("instagram_clicked", config.instagram_url)} />
                  )}
                  {config.google_review_url && (
                    <SocialBtn label="Avaliações" icon={<GoogleIcon size={15}/>}
                      active={clicks.google_review_clicked}
                      bg="#FFFFFF" activeBg="rgba(66,133,244,0.06)"
                      border="#E5E7EB" activeBorder="rgba(66,133,244,0.5)"
                      color="#374151"
                      onClick={() => clickSocial("google_review_clicked", config.google_review_url)} />
                  )}
                  {config.tiktok_url && (
                    <SocialBtn label="TikTok" icon={<TikTokIcon size={13}/>}
                      active={clicks.tiktok_clicked}
                      bg="#FFFFFF" activeBg="rgba(0,0,0,0.04)"
                      border="#E5E7EB" activeBorder="#111827"
                      color="#111827"
                      onClick={() => clickSocial("tiktok_clicked", config.tiktok_url)} />
                  )}
                  {config.whatsapp_number && (
                    <SocialBtn label="WhatsApp" icon={<MessageCircle size={13}/>}
                      active={false}
                      bg="#FFFFFF" activeBg="#FFFFFF"
                      border="#E5E7EB" activeBorder="#E5E7EB"
                      color="#16A34A"
                      onClick={() => window.open(`https://wa.me/${config.whatsapp_number.replace(/\D/g,"")}`, "_blank", "noopener,noreferrer")} />
                  )}
                </div>
              </div>
            )}

            {/* Consent */}
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px", background: "#F9FAFB", borderRadius: "10px", border: "1px solid #F3F4F6" }}>
              <input type="checkbox" id="sc-c" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} style={{ marginTop: "2px", flexShrink: 0, accentColor: "#D32F2F", cursor: "pointer", width: "15px", height: "15px" }} />
              <label htmlFor="sc-c" style={{ fontSize: "0.71rem", lineHeight: 1.55, color: "#6B7280", cursor: "pointer", fontFamily: "Inter,sans-serif" }}>
                {config?.marketing_consent_text || "Autorizo recibir promociones, novedades y beneficios."}
                {config?.privacy_policy_url && <> <a href={config.privacy_policy_url} target="_blank" rel="noopener noreferrer" style={{ color: "#D32F2F", textDecoration: "underline" }}>Política de Privacidad</a>.</>}
              </label>
            </div>

            {error && <p style={{ fontSize: "0.8rem", textAlign: "center", color: "#DC2626", fontFamily: "Inter,sans-serif", margin: 0 }}>{error}</p>}

            {!canSubmit && !submitting && (
              <p style={{ textAlign: "center", fontSize: "0.7rem", color: "#9CA3AF", fontFamily: "Inter,sans-serif", margin: 0 }}>
                Completa tu nombre, WhatsApp y acepta la autorización.
              </p>
            )}

            {/* CTA */}
            <div role="button" tabIndex={canSubmit ? 0 : -1} onClick={doSubmit} onKeyDown={(e) => e.key === "Enter" && doSubmit()}
              style={{
                width: "100%", boxSizing: "border-box",
                minHeight: "58px", padding: "16px 20px", borderRadius: "12px",
                backgroundColor: canSubmit ? "#D32F2F" : "#F3F4F6",
                border: canSubmit ? "2px solid #111827" : "2px solid #E5E7EB",
                color: canSubmit ? "#FFFFFF" : "#9CA3AF",
                fontFamily: "Inter,sans-serif", fontWeight: 800, fontSize: "0.92rem",
                letterSpacing: "0.06em", textTransform: "uppercase",
                cursor: submitting ? "wait" : canSubmit ? "pointer" : "default",
                boxShadow: canSubmit ? "0 4px 16px rgba(211,47,47,0.35)" : "none",
                userSelect: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: "9px",
                transition: "background-color .25s, color .25s, box-shadow .25s, border-color .25s",
              }}>
              {submitting
                ? <span style={{ width: "16px", height: "16px", border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "#FFFFFF", borderRadius: "50%", animation: "spin .7s linear infinite", display: "inline-block", flexShrink: 0 }} />
                : <MessageCircle size={17} />}
              {submitting ? "Enviando..." : "Recibir clave por WhatsApp"}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", paddingTop: "10px", borderTop: "1px solid #F3F4F6" }}>
              <Shield size={11} style={{ color: "#D1D5DB" }} />
              <span style={{ fontSize: "0.62rem", color: "#D1D5DB", fontFamily: "Inter,sans-serif" }}>Tus datos están protegidos · Conexión segura</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
